import { Api, LogManager, ModelRow } from "@mcleod/core";
import { ClickEvent, Label, Snackbar, TableRowDisplayEvent, Toast } from "@mcleod/components";
import { FilterPanel, OrderTabView } from "@mcleod/dispatch";
import { AutogenLayoutInvoiceProcessingProcessing } from "./autogen/AutogenLayoutInvoiceProcessingProcessing";
import { Filter, InvoiceProcessingTableType } from "./InvoiceProcessingTableType";
import { InvoiceProcessingTable, InvoiceProcessingTableListeners, updateFilterCounts, scheduleRefreshWithDelay } from "./InvoiceProcessingUtil";
import { InvoiceProcessingToolPanel } from "./InvoiceProcessingToolPanel";
import { openManageStops } from "./InvoiceProcessing";

const log = LogManager.getLogger("lme.ar.InvoiceProcessingProcessing");

export class InvoiceProcessingProcessing extends AutogenLayoutInvoiceProcessingProcessing implements InvoiceProcessingTable {
    public filterPanel: FilterPanel;
    public type: InvoiceProcessingTableType;
    public static MODE_EDIT: string = "Edit";
    private _selectedMoveId: string = "";

    tableProcessingOnRowDisplay(event: TableRowDisplayEvent): void {
        const tableRow = event.getTableRow();
        const modelRow = event.getTableRow().data as ModelRow;
        tableRow.contextMenuItems = () => InvoiceProcessingToolPanel.getContextMenuItems(this.layoutInvoiceProcessingToolPanel, tableRow);
        InvoiceProcessingTableListeners.invoiceProcessingOnRowDisplayListener(tableRow, modelRow);
        InvoiceProcessingTableListeners.onRowDisplayOrderCount(tableRow, modelRow, this.tableProcessing);
        const labelOrderId = tableRow.findComponentById("labelOrderId") as Label;
        if (labelOrderId) {
            labelOrderId.addUnderlineWhenMouseOver();
        }
        tableRow.addDblClickListener(event => {
            this.selectedMoveId = modelRow.get("movement_id");
        });
    }

    configureTableForType(type: InvoiceProcessingTableType): void {
        this.type = type;
        this.tableProcessing.dataSource.orderBy = this.type.defaultSort;
        this.filterPanel = new FilterPanel(type.secondaryFilters, this.tableProcessing, this.panelSecondaryFilters);
        const originalFilters = this.filterPanel.filters;
        this.filterPanel.updateFilterCounts = () => updateFilterCounts(originalFilters, type, this.filterPanel, this.mainDataSource, false);
        this.layoutInvoiceProcessingToolPanel.addMountListener(() => {
            this.tableProcessing.addMountListener(() => this.layoutInvoiceProcessingToolPanel.initialize(this))
        });
    }

    public get filter(): Filter {
        return this.type.filter;
    }

    onClickOrderIdCount(event: ClickEvent) {
        if (event.target instanceof Label) {
            const orderId = event.target.boundRow.get("order_id")
            this.tableProcessing.setFilter(`order_id=${orderId}`);
        }
    }

    /** This is an event handler for the onClick event of labelOrderId. */
    labelOrderIdOnClick(event: ClickEvent) {
        const orderId = (event.target as Label).text.replace("[", "").replace("]", "");
        OrderTabView.navigateTo({ key: orderId, newTab: true });
    }

/** This is an event handler for the afterCrudClose event of tableProcessing. */
 async  tableProcessingAfterCrudClose(event) {
    const row =
    {
        billing_id:  event.target.layout.sourceBilling.data[0].get("id"),
        order_id: event.target.layout.sourceBilling.data[0].get("order_id"),
        movement_id:  ( event.target.layout.sourceOrders.data.length > 0 )?event.target.layout.sourceOrders.data[0].get("curr_movement_id"):this.selectedMoveId
    };
    const selected = [row];
    const holdChanged = event.target.layout.holdReasonChanged;
    const reasonCode = event.target.layout.sourceBilling.data[0].get("hold_reason_code");
    const reasonCodeText = event.target.layout.sourceBilling.data[0].get("hold_reason_text");

     if ((holdChanged) && (reasonCode)) {
        const result = await Api.post("lme/ar/invoice-on-hold", { billing_id_list: selected, hold_reason_code: reasonCode, hold_reason_text: reasonCodeText, save_billing_row: false });
         if (!result) {
                    Snackbar.showWarningSnackbar("Error placing invoice on hold.");
         } else {
            // Trigger data refresh after successful operation
            this.refreshData();
         }

     }
 }
public get selectedMoveId(): string {
    return this._selectedMoveId;
}
public set selectedMoveId(value: string) {
    this._selectedMoveId = value;
}


    /** This is an event handler for the onClick event of labelStopsPickDrop. */
    labelStopsPickDropOnClick(event: ClickEvent) {
        openManageStops(event);
    }

    private refreshData(recordCount?: number) {
        scheduleRefreshWithDelay(() => this.doRefreshData(), recordCount, "lme.ar.InvoiceProcessingProcessing");
    }

    private doRefreshData() {
        log.debug("Starting data refresh after state change");

        if (!this.mainDataSource) {
            console.error("InvoiceProcessingProcessing: No mainDataSource available for refresh");
            log.error("InvoiceProcessingProcessing: No mainDataSource available for refresh");
            return;
        }

        // Try to find the main InvoiceProcessing layout first
        const mainLayout = this.findMainInvoiceProcessingLayout();
        if (mainLayout) {
            log.debug("Found InvoiceProcessing layout, using searchMainDatasource to preserve profiles");
            mainLayout.searchMainDatasource();
            setTimeout(() => this.updateAllFilterCounts(), 200);
            return;
        }

        // Fallback: Find profile from parent hierarchy
        const profileRow = this.findProfileFromParent();
        if (profileRow) {
            log.debug("Using profile from parent layout");
            this.executeDataRefresh({ profile_row: profileRow }, "profile from parent layout");
            return;
        }

        // Last resort: Generic refresh (will reset to default profile)
        log.debug("No profile found, performing generic refresh (will reset to default profile)");
        this.executeDataRefresh({}, "generic refresh (will reset to default profile)");
    }

    /**
     * Finds the main InvoiceProcessing layout by walking up the parent hierarchy
     */
    private findMainInvoiceProcessingLayout(): any {
        let layout = this as any;
        while (layout && layout.parent) {
            layout = layout.parent;
            if (layout.constructor.name === 'InvoiceProcessing' || layout.searchMainDatasource) {
                return layout;
            }
        }
        return null;
    }

    /**
     * Finds a profile row from the parent hierarchy
     */
    private findProfileFromParent(): any {
        let currentLayout = this as any;
        while (currentLayout && currentLayout.parent) {
            currentLayout = currentLayout.parent;
            if (currentLayout.profileRow || currentLayout._profileRow) {
                const profileRow = currentLayout.profileRow || currentLayout._profileRow;
                if (profileRow && profileRow.get && profileRow.get("id")) {
                    return profileRow;
                }
            }
        }
        return null;
    }

    /**
     * Executes the data refresh with the given search parameters
     */
    private executeDataRefresh(searchParams: any, refreshType: string) {
        this.mainDataSource.clear();
        this.mainDataSource.search(searchParams)
            .then(() => {
                log.debug(`${refreshType} completed, updating filter counts`);
                this.updateAllFilterCounts();
            })
            .catch(error => {
                console.error(`Error during ${refreshType}:`, error);
                log.error(`Error during ${refreshType}:`, error);
            });
    }

    private updateAllFilterCounts() {
        // Force update of all filter counts to ensure tabs reflect current data
        log.debug("InvoiceProcessingProcessing: Starting updateAllFilterCounts");

        setTimeout(() => {
            try {
                // Update filter counts for this tab
                if (this.filterPanel && this.filterPanel.updateFilterCounts) {
                    log.debug("InvoiceProcessingProcessing: Updating filter counts for this tab");
                    this.filterPanel.updateFilterCounts();
                }

                // Try to update filter counts for all tabs by walking up to find other tab layouts
                let parentLayout = this as any;
                let tabCount = 0;

                while (parentLayout && parentLayout.parent) {
                    parentLayout = parentLayout.parent;

                    // Look for other tab layouts (OnHold, ReadyToProcess, etc.)
                    if (parentLayout.children) {
                        for (const child of parentLayout.children) {
                            if (child.filterPanel && child.filterPanel.updateFilterCounts) {
                                log.debug(`InvoiceProcessingProcessing: Updating filter counts for child tab: ${child.constructor.name}`);
                                child.filterPanel.updateFilterCounts();
                                tabCount++;
                            }

                            // Also try to update child tabs' data sources directly
                            if (child.tableProcessing && child.tableProcessing.dataSource) {
                                try {
                                    log.debug(`InvoiceProcessingProcessing: Triggering data source refresh for: ${child.constructor.name}`);
                                    child.tableProcessing.dataSource.refresh();
                                } catch (refreshError) {
                                    console.log(`Could not refresh data source for ${child.constructor.name}:`, refreshError);
                                }
                            }
                        }
                    }

                    // Also check if this parent layout itself has a filter panel
                    if (parentLayout.filterPanel && parentLayout.filterPanel.updateFilterCounts) {
                        log.debug(`InvoiceProcessingProcessing: Updating filter counts for parent: ${parentLayout.constructor.name}`);
                        parentLayout.filterPanel.updateFilterCounts();
                        tabCount++;
                    }
                }

                log.debug(`InvoiceProcessingProcessing: Updated filter counts for ${tabCount} tabs`);

            } catch (error) {
                console.error("Error updating filter counts:", error);
                log.error("Error updating filter counts:", error);
            }
        }, 100);

        // Also do a second pass with a longer delay to catch any delayed updates
        setTimeout(() => {
            try {
                log.debug("InvoiceProcessingProcessing: Second pass filter count update");
                if (this.filterPanel && this.filterPanel.updateFilterCounts) {
                    this.filterPanel.updateFilterCounts();
                }
                // Force refresh all sibling tabs
                this.forceRefreshAllSiblingTabs();
            } catch (error) {
                console.error("Error in second pass filter count update:", error);
                log.error("Error in second pass filter count update:", error);
            }
        }, 500);
    }

    private forceRefreshAllSiblingTabs() {
        try {
            let parentLayout = this as any;
            while (parentLayout && parentLayout.parent) {
                parentLayout = parentLayout.parent;
                if (parentLayout.children) {
                    for (const child of parentLayout.children) {
                        if (child !== this && child.filterPanel && child.filterPanel.updateFilterCounts) {
                            log.debug(`InvoiceProcessingProcessing: Force refreshing sibling tab: ${child.constructor.name}`);
                            child.filterPanel.updateFilterCounts();
                        }
                    }
                }
            }
        } catch (error) {
            console.log("Error in forceRefreshAllSiblingTabs:", error);
        }
    }

}
